import { g, perpetualOptions } from '../global-constants';

// A whitespace-only string (e.g. "   ") is truthy in JS and would otherwise
// slip past `value || null` checks, landing in the schema unomitted.
const blankToNull = (value) => {
	if (typeof value !== 'string') return value ?? null;
	const trimmed = value.trim();
	return trimmed || null;
};

const stripHtml = (html) => {
	if (!html) return null;
	const stripped = html
		.replace(/<[^>]*>/g, ' ')
		.replace(/\s+/g, ' ')
		.trim();
	return stripped || null;
};

const isDisplayableDate = (value) =>
	Boolean(value) &&
	value !== perpetualOptions.NO_SHOW &&
	value !== perpetualOptions.NO_SHOW_ON_SPECIAL;

const formatDateRange = (startDateDisplay, endDateDisplay) => {
	const hasStart = isDisplayableDate(startDateDisplay);
	const hasEnd = isDisplayableDate(endDateDisplay);

	if (hasStart && hasEnd) {
		return `Offer valid on ${startDateDisplay} through ${endDateDisplay}`;
	}
	if (hasEnd) {
		return `Offer valid through ${endDateDisplay}`;
	}
	if (hasStart) {
		return `Offer valid on ${startDateDisplay}`;
	}
	return null;
};

const formatIsoDate = (isoDate) => {
	if (!isoDate) return null;
	const date = new Date(isoDate);
	if (Number.isNaN(date.getTime())) return null;
	return date.toLocaleDateString('en-US', { timeZone: 'UTC' });
};

const formatDiscount = (discountType, discountValue) => {
	if (!discountType || discountValue == null) return null;
	switch (discountType.toUpperCase()) {
		case 'AMOUNT':
			return `$${discountValue} off`;
		case 'PERCENT':
			return `${discountValue}% off`;
		case 'FIXED':
			return `$${discountValue}`;
		case 'STARTING_AT':
			return `Starting at $${discountValue}`;
		default:
			return null;
	}
};

// Single extraction step shared by the sr-only text and the schema.org/Offer
// JSON-LD generator, so the two can never disagree (US2046179/US2063265).
export const extractOfferFields = (promo) => {
	if (!promo) return null;

	const {
		type,
		title,
		description,
		disclaimer,
		additionalDetails,
		discountType,
		discountValue,
		couponCode,
		startDate,
		startDateDisplay,
		endDate,
		endDateDisplay,
		incentiveData
	} = promo;

	const segments = [];
	let name = null;
	let offerDescription = null;
	let validFrom = null;
	let validThrough = null;
	let price = null;
	let model = null;
	let modelYear = null;

	if (type === g.INCENTIVE) {
		name =
			blankToNull(incentiveData?.title) ||
			blankToNull(incentiveData?.shortTitle);
		offerDescription = blankToNull(incentiveData?.offerDetails);
		const displayStartDate = formatIsoDate(
			incentiveData?.incentiveEffectiveDate
		);
		const displayEndDate = formatIsoDate(
			incentiveData?.incentiveExpirationDate
		);
		const incentiveDisclaimer = stripHtml(incentiveData?.disclaimer);

		if (name) segments.push(name);
		if (offerDescription) segments.push(offerDescription);
		const dateRange = formatDateRange(displayStartDate, displayEndDate);
		if (dateRange) segments.push(dateRange);
		if (incentiveDisclaimer)
			segments.push(`Disclaimer: ${incentiveDisclaimer}`);

		if (isDisplayableDate(displayStartDate)) {
			validFrom = incentiveData?.incentiveEffectiveDate;
		}
		if (isDisplayableDate(displayEndDate)) {
			validThrough = incentiveData?.incentiveExpirationDate;
		}
	} else {
		name = blankToNull(title);
		if (name) segments.push(name);
		if (type === g.SERVICE || type === g.PARTS) {
			const discountLabel = formatDiscount(discountType, discountValue);
			if (discountLabel) segments.push(discountLabel);
			const cleanCouponCode = blankToNull(couponCode);
			if (cleanCouponCode)
				segments.push(`Coupon code: ${cleanCouponCode}`);
			if (
				discountType?.toUpperCase() === 'FIXED' &&
				discountValue != null
			) {
				price = String(discountValue);
			}
		}
		offerDescription = blankToNull(description);
		if (offerDescription) segments.push(offerDescription);
		const dateRange = formatDateRange(startDateDisplay, endDateDisplay);
		if (dateRange) segments.push(dateRange);
		const cleanDisclaimer = blankToNull(disclaimer);
		if (cleanDisclaimer) segments.push(`Disclaimer: ${cleanDisclaimer}`);
		const cleanAdditionalDetails = blankToNull(additionalDetails);
		if (cleanAdditionalDetails) segments.push(cleanAdditionalDetails);

		if (isDisplayableDate(startDateDisplay)) {
			validFrom = startDate;
		}
		if (isDisplayableDate(endDateDisplay)) {
			validThrough = endDate;
		}
	}

	const srOnlyText = segments.length
		? segments
				.map((s) => s.trim().replace(/\.$/, ''))
				.filter(Boolean)
				.join('. ')
		: null;

	if (type === g.INCENTIVE && srOnlyText) {
		const trimModel = blankToNull(incentiveData?.trim?.model);
		if (trimModel && srOnlyText.includes(trimModel)) {
			model = trimModel;
			modelYear = incentiveData?.trim?.year || null;
		}
	}

	return {
		srOnlyText,
		name,
		description: offerDescription,
		validFrom: validFrom || null,
		validThrough: validThrough || null,
		price,
		model,
		modelYear
	};
};

export const buildSrOnlyText = (promo) =>
	extractOfferFields(promo)?.srOnlyText ?? null;
